import { quantileSeq } from 'mathjs';

import { defaultVariables } from '../constants';
import AxiosInstance from '@axios';

export const getExperimentsRequestParams = (search, allVariables) => {
  const params = search.slice(1, search.length).split('&')
    .map((param) => {
      const [ name, value ] = param.split('=');
      return { name, value };
    })
    .filter((param) => !!param.value);
      
  let machines = params.find(({ name }) => name === 'machines');

  machines = machines ? decodeURIComponent( machines.value.replace(/\+/g, '%20') )?.split('.') : [];

  let page = params.find(({ name }) => name === 'page');
  page = page ? page.value : 1;

  let variables = [];
  if (params.find(({ name }) => name === 'varIds')) {
    variables = params
      .find(({ name }) => name === 'varIds').value
      .split(/\+|%2B/);
    variables.unshift(9);
  }
  else {
    variables = defaultVariables;
  }

  const start_date = params.find(({ name }) => name === 'start_date')?.value;
  const end_date = params.find(({ name }) => name === 'end_date')?.value;
  const pulse = parseInt(params.find(({ name }) => name === 'pulse')?.value);
  const run = parseInt(params.find(({ name }) => name === 'run')?.value);

  const conditions = params
    .filter(({ name }) => ! [ "machines", "page", "varIds", "start_date", "end_date", "pulse", "run" ].includes(name))
    .map(({ name, value }) => {
      if (!name.includes('min') && !name.includes('max')) {
        return {
          variable_id: name,
          condition: '=',
          value: value
        };
      }
      else {
        const varName = name.slice(0, name.length - 4);
        const condition = name.slice(-3);

        return {
          variable_id: varName,
          condition: condition === 'min' ? '>=' : '<=',
          value: isNaN(value) ? value : parseFloat(value)
        };
      }
    });

  return {
    conditions,
    machines,
    start_date,
    end_date,
    pulse,
    run,
    page,
    variables
  };
};

export const formatDate = (dateStr) => {
  return ( dateStr ? `${dateStr.slice(0,4)}-${dateStr.slice(5,7)}-${dateStr.slice(8,10)}` : "" );
};

export const getXAxisTicks = (data) => {
  data = data && data.length && data[0].time!==undefined ? data : [ { time: 0 } ];
  const time = data.map(val => val.time);
  const minTime = Math.min(...time);
  const maxTime = Math.max(...time);

  if (maxTime - minTime > 1) {
    if (maxTime - minTime <= 2) {
      const ticks = [];

      for (let i = Math.floor(minTime); i <= maxTime+0.25; i=i+0.25) {
        ticks.push(i);
      }
      return ticks;
    }
    if (maxTime - minTime <= 4) {
      const ticks = [];

      for (let i = Math.floor(minTime); i <= maxTime+0.5; i=i+0.5) {
        ticks.push(i);
      }
      return ticks;
    }
    if (maxTime - minTime <= 6) {
      const ticks = [];
      
      for (let i = Math.floor(minTime); i <= Math.ceil(maxTime); i++) {
        ticks.push(i);
      }
      return ticks;
    }
    else {
      const ticks = [];

      for (let i = Math.floor(minTime); i <= Math.ceil(maxTime); i += (Math.floor((maxTime - minTime)/8) || 1)) {
        ticks.push(i);
      }
      return ticks;
    }
  }

  return quantileSeq(time, [ 0, 1 / 4, 1 / 2, 3 / 4, 1 ]).map((val) => {
    return val.toFixed(2);
  });
};

export const range = (from, to) => {
  let i = from;
  const range = [];

  while (i < to) {
    range.push(i);
    i++;
  }

  return range;
};

export const getFileName = (idsField, pulse, run, machine) => {
  return `${pulse}_${run}_${machine}_${idsField.replace(/\//g, '_')}`;
};

export const getExperimentInfoFromUri = (uri) => {
  const params = uri.split('?')[1].split(';');
  const obj = {};

  params.forEach(param => {
    const [ name, value ] = param.split('=');
    obj[name] = value;
  });

  return obj;
};

export const getEntryDataRequestParams = (search) => {
  const params = new URLSearchParams(search);
  let filters = [];
  let page = 1;

  if (params.has('name')) {
    filters = [ { ids_field: params.get('name') } ];
  }

  if (params.has('page')) {
    page = Number(params.get('page'));
  }

  return {
    filters,
    page
  };
};

export const filterChartDataByIdsField = (chartData, fieldName) => {
  if (chartData.length === 0) {
    return null;
  }
  return chartData.find(({ ids_field }) => ids_field === fieldName);
};

export const filterChartDataByVarId = (chartData, varId) => {
  if (chartData.length === 0) {
    return null;
  }
  return chartData.find(({ id }) => id === varId);
};

export const getChartTitle = (pulse, run, machine, idsField) => {
  return `Pulse: ${pulse} Run: ${run} Machine: ${machine}, Variable: ${idsField}`;
};

export const getInputType = (type) => {
  if ([ 'double', 'integer' ].includes(type)) {
    return 'number';
  }
  return 'text';
};

export const getFormControls = (varIds, variablesTypes, allVariables) => {
  return varIds.map((idVar) => {
    const variable = allVariables.find(({ id }) => ""+id === idVar);
    if (!variable) return undefined;
    const variableType = variablesTypes[variable.type];
    
    return {
      cType: getInputType(variableType?.value),
      ...variable
    };
  }).filter(field => field);
};

export const getIdsFieldsFromParams = (params) => {
  if (params && params.has('varIds')) {
    return params.get('varIds').replace(/\./g, '/').split(/\+|\s/);
  }
  return [];
};

export const getFiltersFromParams = (params) => {
  return Array.from(params.keys())
    .filter(key => key !== 'page' && key !== 'varIds' && key !== 'machines')
    .map(key => ({
      [key.replace(/\./g, '/')]: params.get(key)
    }))
    .reduce((a, v) => ({ ...a, ...v }), {});
};

export const getUrlParamsFromFormControls = (formControls) => 
  formControls.map(({ id }) => id).join('+');

export const getMachinesFromParams = (params) => {
  if (params.has('machines') && params.get('machines') !== '') {
    return params.get('machines').split('.').map(name => ({
      label: name,
      value: name
    }));
  }
  return [];
};

export const getValuesFromLabel = (activeLabel) => {
  const [ min, max ] = activeLabel.slice(1, activeLabel.length - 1).split(', ');
  return { min, max };
};

export const getVariableNameFromIdsField = (variables, idsField) => {
  return variables?.data?.find((variable) => variable.ids_field === idsField)?.name;
};

export const getVariableUnitFromIdsField = (variables, idsField) => {
  return variables?.data?.find((variable) => variable.ids_field === idsField)?.unit;
};

export const getVariableDefinitionFromIdsField = (variables, idsField) => {
  return variables?.data?.find((variable) => variable.ids_field === idsField)?.definition;
};

const engineeringFormat = new Intl.NumberFormat('en', { notation: 'engineering' });

export const getNumberInFormat = (value, notation = 'engineering') => {
  if (value !== '' && notation === 'engineering') return engineeringFormat.format(value)?.replace(/E0$/, '');

  return value;
};
export const getTimeInFormat = (value) => {
  return (""+value).substring(0,6);
};

export const mapDataToSerie = (data) => {
  return data?.value?.map( (value, index) => {
    const error_upper = data.error_upper?.[index] ?? null;
    const error_lower = data.error_lower?.[index] ?? error_upper;

    return {
      "time": data.time?.[index],
      "value": value,
      "error": error_lower !== null && error_upper !== null ? [ error_lower, error_upper ] : null,
    };
  });
};


/* new roles system */

export const ROLE_ADMIN = "admin";
export const ROLE_DATA_MANAGER = "data_manager";
export const ROLE_CSV_EXPORTER = "csv_exporter";
export const ROLE_ANNOTATION_MANAGER = "annotation_manager";
export const ROLE_URI_MANAGER = "uri_manager";

export const checkAccess = async (functionality) => {
  const userRoles = await getUserRoles();
  return userRoles?.includes(functionality);
};

const getUserRoles = async () => {
  try {
    const response = await AxiosInstance({ method: "get", url: "/users/me" });
    if (response?.status===200) {
      const roles = response.data?.roles;
      if (Array.isArray(roles)) {
        const rolesSimple = roles.map(role => role.name);
        if (rolesSimple.includes(ROLE_ADMIN)) {
          rolesSimple.push(ROLE_DATA_MANAGER, ROLE_CSV_EXPORTER, ROLE_ANNOTATION_MANAGER, ROLE_URI_MANAGER);
        }
        return rolesSimple;
      }
    }
    return [];
  }
  catch ( error) { return []; }
};